import { useGameStore } from '@/stores';
import { Team } from '@/types/player';
import './TeamInfoBar.css';

interface TeamInfoBarProps {
  /** Whether this bar is rendered at the top or the bottom of the screen. */
  position: 'top' | 'bottom';
}

/**
 * TeamInfoBar - Top/bottom info strip showing team label, turn indicator,
 * each player's hand count and (top bar only) the current round number.
 *
 * Redesign v3:
 *  - 装飾は `hud/top-bar.png` の baked-in テキスト (TEAM 1 / DUO / LAW ENFORCEMENT)
 *  - 動的値 (ROUND 番号 / 手札合計) は PNG の対応セル上に React overlay で
 *    マスクと一緒に重ねる（焼き付け文字を覆い隠して上書き）
 */
const TeamInfoBar = ({ position }: TeamInfoBarProps) => {
  const gameState = useGameStore((state) => state.gameState);
  const matchState = useGameStore((state) => state.matchState);
  const localPlayerId = useGameStore((state) => state.localPlayerId);

  // Redesign v3: 下部バーは撤去（PLAYER 情報は WANTED フレームへ移行）
  if (position === 'bottom') {
    return null;
  }

  if (!gameState || !localPlayerId) {
    return null;
  }

  const localPlayer = gameState.players.find((p) => p.id === localPlayerId);
  if (!localPlayer) {
    return null;
  }

  const localTeam = localPlayer.team;
  // 上部バーは常に「敵チーム」を表示（下部バーは redesign v3 で撤去済）
  const targetTeam: Team =
    localTeam === Team.TEAM_1 ? Team.TEAM_2 : Team.TEAM_1;

  const teamPlayers = gameState.players.filter((p) => p.team === targetTeam);
  if (teamPlayers.length === 0) {
    return null;
  }

  // Hand counts: DUO shows individual counts joined by '+', SOLO shows a single value.
  const handCountText = teamPlayers.map((p) => p.hand?.length ?? 0).join('+');

  const roundNumber = matchState?.roundNumber ?? 1;
  const roundDisplay = String(roundNumber).padStart(2, '0');

  return (
    <div
      className={`TeamInfoBar TeamInfoBar--${position} TeamInfoBar--team-${targetTeam}`}
      role="status"
      aria-live="polite"
      aria-label={`敵チーム${targetTeam === Team.TEAM_1 ? ' 警察' : ' 犯人'} ラウンド${roundNumber} 手札${handCountText}`}
    >
      {/* PNG 背景レイヤ (mix-blend-mode: screen) */}
      <div className="TeamInfoBar__bg" aria-hidden="true" />

      {/* 動的値オーバーレイ — PNG の baked-in セル位置に合わせて配置 */}
      <div className="TeamInfoBar__overlay" aria-hidden="true">
        <div className="TeamInfoBar__round-cell">
          <span className="TeamInfoBar__round-num">{roundDisplay}</span>
        </div>
        <div className="TeamInfoBar__handcount-cell">
          <span className="TeamInfoBar__handcount">{handCountText}</span>
        </div>
      </div>
    </div>
  );
};

export default TeamInfoBar;
